import { type Lesson } from "@core/grades/types";
import { formatDuration, formatNumber, money, URLS } from "@core/shared/utils";
import { BaseLink } from "@design-system/components/link";
import { platformFeaturesAtom, settingsAtom } from "@framework/shared/atoms";
import { trans } from "@mongez/localization";
import {
  Clock3Icon,
  EyeIcon,
  MessageCircleIcon,
  PlayIcon,
  UserRoundIcon,
} from "lucide-react";
import { LessonLikeButton } from "../LessonLikeButton";

type CategoryLessonCardProps = {
  index: number;
  lesson: Lesson;
};

export function CategoryLessonCard({ index, lesson }: CategoryLessonCardProps) {
  const commentsEnabled =
    platformFeaturesAtom.useValue().commentsAndRatings === true;
  const lessonImage =
    lesson.image?.url || settingsAtom.get("general")?.logo?.url;

  return (
    <article className="hover:shadow-primary-950/10 group flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-gray-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-xl dark:border-gray-800 dark:bg-gray-900 dark:hover:border-primary-800">
      <BaseLink
        aria-label={lesson.name}
        className="relative block aspect-video overflow-hidden bg-gradient-to-br from-primary-500 to-violet-600 hover:no-underline"
        to={URLS.lessons.view(lesson)}
      >
        {lessonImage ? (
          <img
            alt={lesson.name}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            loading="lazy"
            src={lessonImage}
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950/75 via-gray-950/5 to-transparent" />
        <span className="absolute start-3 top-3 flex h-9 min-w-9 items-center justify-center rounded-xl border border-white/20 bg-gray-950/50 px-2 text-xs font-black text-white backdrop-blur">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="absolute bottom-3 start-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-[11px] font-black text-primary-800 shadow-lg">
          <PlayIcon className="h-3 w-3 fill-current" />
          {trans("categoryDetails.startLesson")}
        </span>
        {lesson.duration ? (
          <span
            className="absolute bottom-3 end-3 inline-flex items-center gap-1 rounded-full bg-gray-950/70 px-2.5 py-1.5 text-[11px] font-bold text-white backdrop-blur"
            dir="ltr"
          >
            <Clock3Icon className="h-3 w-3" />
            {formatDuration(lesson.duration)}
          </span>
        ) : null}
      </BaseLink>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-start justify-between gap-3">
          <BaseLink
            className="min-w-0 hover:no-underline"
            to={URLS.lessons.view(lesson)}
          >
            <h3 className="line-clamp-2 text-base font-black leading-6 text-gray-950 transition group-hover:text-primary-700 dark:text-white dark:group-hover:text-primary-300">
              {lesson.name}
            </h3>
          </BaseLink>
          <strong
            className={`shrink-0 text-xs font-black ${
              lesson.isFree
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-gray-950 dark:text-white"
            }`}
          >
            {lesson.isFree
              ? trans("categoryDetails.freeLesson")
              : money(lesson.cost)}
          </strong>
        </div>

        <div className="mt-4 flex items-center gap-2">
          {lesson.instructor?.image?.url ? (
            <img
              alt={lesson.instructor.name}
              className="h-8 w-8 rounded-full object-cover ring-2 ring-primary-100 dark:ring-primary-900"
              loading="lazy"
              src={lesson.instructor.image.url}
            />
          ) : (
            <span className="dark:bg-primary-950/50 flex h-8 w-8 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:text-primary-300">
              <UserRoundIcon className="h-4 w-4" />
            </span>
          )}
          <div className="min-w-0">
            <p className="truncate text-xs font-bold text-gray-700 dark:text-gray-200">
              {lesson.instructor?.name}
            </p>
            <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">
              {lesson.subject?.name}
            </p>
          </div>
        </div>

        <div className="mt-auto flex items-center gap-4 border-t border-gray-100 pt-4 text-[11px] font-semibold text-gray-500 dark:border-gray-800 dark:text-gray-400">
          <span className="inline-flex items-center gap-1" dir="ltr">
            <EyeIcon className="h-3.5 w-3.5" />
            {formatNumber(lesson.views || 0)}
          </span>
          {commentsEnabled ? (
            <span className="inline-flex items-center gap-1" dir="ltr">
              <MessageCircleIcon className="h-3.5 w-3.5" />
              {formatNumber(lesson.comments || 0)}
            </span>
          ) : null}
          <LessonLikeButton lesson={lesson} compact />
        </div>
      </div>
    </article>
  );
}
